/**
 * Side-nav model from module manifests (BUILD-PLAN 3.4 groups). Items the
 * user lacks permission for are hidden; Marketplace modules the offices in
 * scope do not have show an "Add-on" hint and open their upsell panel, or
 * are left out when the practice chose "Hide modules we don't use".
 * Modules set live counts with `useNavBadge`.
 */
import type { NavGroup as ManifestNavGroup } from '@velora/contracts';
import { ROLE_MODULES } from '@velora/contracts/identity';
import type { ShellBootstrap } from '@velora/contracts/shell';
import { isMarketplaceModuleKey } from '@velora/contracts/marketplace';
import { isTermKey, type TermKey } from '@velora/contracts/skins/terms';
import type { NavGroup } from '@velora/ui';
import { useEffect } from 'react';
import { Icon } from '../icons.js';
import type { WebModule } from '../module.js';
import { buildNav } from '../modules.js';
import { isBarePath } from './barePaths.js';
import { createRegistry } from '../registry/store.js';
import { canInScope, entitlementFor } from '../session/access.js';
import type { NavEntryIn } from './sidenav/model.js';

export const GROUP_LABELS: Record<ManifestNavGroup, string | undefined> = {
  dashboard: undefined,
  schedule: undefined,
  patients: 'Patients',
  clinical: 'Clinical',
  revenue: 'Revenue',
  operations: 'Operations',
  analytics: 'Insights',
  // Marketplace and Settings share one "Platform" section (prototype nav).
  marketplace: 'Platform',
  meta: 'Product',
  settings: 'Platform',
};

/**
 * Admin pages that live in the Settings section rail: when the person can
 * open Settings, the side nav shows that one entry instead of repeating the
 * rail (Users, Roles, Offices, PulseSync, Audit log...), so Insights and
 * Platform stay above the fold.
 */
function collapseAdmin<T extends { moduleId: string; group: ManifestNavGroup }>(
  items: readonly T[],
  visible: (item: T) => boolean,
): T[] {
  const settings = items.find((i) => i.group === 'settings' && i.moduleId === 'settings');
  if (!settings || !visible(settings)) return [...items];
  return items.filter((i) => i.group !== 'settings' || i === settings);
}

export interface NavBadge {
  /** Module id the badge belongs to. */
  id: string;
  count: number;
  tone?: 'neutral' | 'danger';
  /** Hover text on the nav item (e.g. which AI Receptionist modes are on). */
  hint?: string;
}

export const navBadges = createRegistry<NavBadge>();

/** Shows `count` on the module's nav item while the caller is mounted (0 hides it). */
export function useNavBadge(
  moduleId: string,
  count: number,
  tone: 'neutral' | 'danger' = 'neutral',
) {
  useEffect(() => navBadges.register({ id: moduleId, count, tone }), [moduleId, count, tone]);
}

/** Hover text on the module's nav item while the caller is mounted (empty hides it). */
export function useNavHint(moduleId: string, hint: string) {
  useEffect(
    () => (hint ? navBadges.register({ id: moduleId, count: 0, hint }) : undefined),
    [moduleId, hint],
  );
}

export interface NavModel {
  groups: NavGroup[];
  /**
   * The same items flat, with what the Velora Modern side nav needs to
   * arrange them (sidenav/model.ts): manifest group and icon name, and
   * whether the item is a Marketplace add-on.
   */
  entries: Array<NavEntryIn & { iconNode: NavGroup['items'][number]['icon'] }>;
  /** Nav item id -> path, to work out the active item. */
  paths: Array<{ id: string; path: string }>;
}

export function buildNavModel(
  modules: readonly WebModule[],
  session: Pick<
    ShellBootstrap,
    'permissions' | 'locationPermissions' | 'entitlements' | 'trialsAvailable'
  > &
    Partial<Pick<ShellBootstrap, 'marketplace' | 'hiddenModules'>>,
  locationIds: readonly string[],
  badges: readonly NavBadge[],
  /** The active mode's wording for items that name a `labelKey`. */
  term?: (key: TermKey) => string,
): NavModel {
  // A route may carry its own Marketplace key (AI scribe inside the AI
  // workspace); the module's key applies otherwise.
  const entitlementKeys = new Map(
    modules.flatMap((m) =>
      m.manifest.routes.map((r) => [
        `${m.manifest.id}:${r.id}`,
        r.entitlementKey !== undefined ? r.entitlementKey : m.manifest.entitlementKey,
      ]),
    ),
  );
  const badgeOf = new Map(badges.map((b) => [b.id, b]));
  const merged = new Map<string, NavGroup & { items: NavGroup['items'][number][] }>();
  const paths: NavModel['paths'] = [];
  const entries: NavModel['entries'] = [];

  // Modules the person's roles hide (Settings > Roles > Module access).
  const hiddenNav = new Set(
    ROLE_MODULES.filter((m) => session.hiddenModules?.includes(m.key)).flatMap((m) => m.navModules),
  );
  const allowed = (item: { permission?: string | undefined; moduleId?: string }) =>
    !(item.moduleId && hiddenNav.has(item.moduleId)) &&
    (!item.permission || canInScope(session, item.permission, locationIds));
  const hideLocked = session.marketplace?.hideLockedNav === true;
  for (const { group, items: all } of buildNav(modules)) {
    const items = group === 'settings' ? collapseAdmin(all, allowed) : all;
    const label = GROUP_LABELS[group];
    // Dashboard and Schedule share the unlabelled top section.
    const key = label ?? 'top';
    const target = merged.get(key) ?? { id: key, ...(label ? { label } : {}), items: [] };
    for (const item of items) {
      if (!allowed(item)) continue;
      const entKey = entitlementKeys.get(`${item.moduleId}:${item.routeId}`) ?? null;
      const ent = entitlementFor(session, entKey, locationIds);
      if (hideLocked && !ent.readable && entKey && isMarketplaceModuleKey(entKey)) continue;
      const badge = badgeOf.get(item.moduleId);
      const id = `${item.moduleId}:${item.routeId}`;
      const shared = {
        id,
        label: term && item.labelKey && isTermKey(item.labelKey) ? term(item.labelKey) : item.label,
        href: item.path,
        ...(badge && badge.count > 0
          ? { badge: badge.count, badgeTone: badge.tone ?? 'neutral' }
          : {}),
        ...(badge?.hint ? { hint: badge.hint } : {}),
        ...(ent.readable ? {} : { locked: true }),
      };
      const iconNode = <Icon name={item.icon} size={17} />;
      target.items.push({ ...shared, icon: iconNode });
      entries.push({
        ...shared,
        moduleId: item.moduleId,
        group,
        icon: item.icon,
        iconNode,
        ...(entKey && isMarketplaceModuleKey(entKey) && ent.readable ? { addOn: true } : {}),
      });
      paths.push({ id, path: item.path });
    }
    if (target.items.length > 0) merged.set(key, target);
  }
  return { groups: [...merged.values()], entries, paths };
}

function longestPrefix(paths: NavModel['paths'], pathname: string): string | undefined {
  let best: { id: string; len: number } | undefined;
  for (const { id, path } of paths) {
    const hit = pathname === path || pathname.startsWith(path === '/' ? '/' : `${path}/`);
    if (hit && (!best || path.length > best.len)) best = { id, len: path.length };
  }
  return best?.id;
}

/**
 * The active nav item. When the module that owns the matched route has a nav
 * item, that module's item wins (its longest matching path, else its first
 * item): `/patients/$patientId/chart` belongs to the chart, so Charting is
 * active, not Patients. Otherwise the item whose path is the longest prefix.
 */
export function activeNavId(
  paths: NavModel['paths'],
  pathname: string,
  ownerModuleId?: string,
): string | undefined {
  if (ownerModuleId) {
    const own = paths.filter((p) => p.id.startsWith(`${ownerModuleId}:`));
    if (own.length > 0) return longestPrefix(own, pathname) ?? own[0]?.id;
  }
  return longestPrefix(paths, pathname);
}

/** A `nav.<module>` / `nav.<module>.<route>` action, before permission and admin collapsing. */
export interface NavActionSpec {
  id: string;
  label: string;
  href: string;
  /** The nav item's (else the route's) permission; the shell greys or hides it. */
  permission?: string;
}

/**
 * Every navigation action the familiar menus can name, built from the
 * manifests themselves and not from the side nav: the side nav collapses
 * the admin pages into Settings (`collapseAdmin`) and leaves out routes with
 * no nav item (Payments), and a menu item pointing at one of those was
 * silently dropped (ADM-14, HA-16: OD Tools > Database Maintenance, Lists >
 * Insurance Carriers, Eaglesoft Utilities > Audit Log...).
 *
 * `nav.<module>.<route>` for each nav item, then for each other route without
 * path parameters; `nav.<module>` is the module's first nav item, else its
 * first such route. Sign-in pages are left out.
 */
export function buildNavActions(
  modules: readonly WebModule[],
  term?: (key: TermKey) => string,
): NavActionSpec[] {
  const out = new Map<string, NavActionSpec>();
  const add = (moduleId: string, routeId: string, spec: Omit<NavActionSpec, 'id'>) => {
    const full = `nav.${moduleId}.${routeId}`;
    if (!out.has(full)) out.set(full, { id: full, ...spec });
    const short = `nav.${moduleId}`;
    if (!out.has(short)) out.set(short, { id: short, ...spec });
  };
  for (const { items } of buildNav(modules)) {
    for (const item of items) {
      // Bare pages (sign-in, the C2G Admin console) open outside the shell.
      if (isBarePath(item.path)) continue;
      add(item.moduleId, item.routeId, {
        label: term && item.labelKey && isTermKey(item.labelKey) ? term(item.labelKey) : item.label,
        href: item.path,
        ...(item.permission ? { permission: item.permission } : {}),
      });
    }
  }
  for (const { manifest } of modules) {
    for (const route of manifest.routes) {
      if (route.path.includes('$') || isBarePath(route.path)) continue;
      add(manifest.id, route.id, {
        label: route.title,
        href: route.path,
        ...(route.permission ? { permission: route.permission } : {}),
      });
    }
  }
  return [...out.values()];
}
